import useAccountDocumentTypes from '@UI/Hooks/useAccountDocumentTypes';
import useDocumentRoute from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Hooks/useDocumentRoute';
import type { ReviewStage } from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Hooks/useReviewStage';
import ReviewStageTab from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/ReviewStage/ReviewStageTab';
import { theme } from '@Guidelines/Theme/theme';
import { css } from '@emotion/react';
import * as React from 'react';

const tabsStyle = css`
	display: flex;
	gap: 0.5rem;
	flex-wrap: wrap;
`;

const failureStyle = css`
	margin: 0.5rem 0 0;
	font-family: ${theme.fonts.body};
	font-size: 0.78125rem;
	color: ${theme.states.danger.solid};
`;

type Props = {
	readonly reviewStage: ReviewStage;
};

/**
 * The documents of the stage, one tab each: they are verified together. Absent for a one-document stage.
 * Renders the documents `useReviewStage` read from the preparation; a preparation that cannot be read
 * only costs these tabs, the document itself stays reviewable on its own.
 */
const ReviewStageTabs: React.FC<Props> = ({ reviewStage }) => {
	const { subject, documentId } = useDocumentRoute();
	const { documentTypes } = useAccountDocumentTypes();

	if (reviewStage.documents.length < 2) {
		return null;
	}

	const failedDocuments = reviewStage.documents.filter((document) => document.isFailed);

	return (
		<React.Fragment>
			<div role="tablist" aria-label="Documents de l’étape" css={tabsStyle}>
				{reviewStage.documents.map((document) => (
					<ReviewStageTab
						key={document.stepId}
						subject={subject}
						preparationId={reviewStage.preparationId}
						stage={reviewStage.stage}
						document={document}
						label={documentTypes?.labelOf(document.documentTypeId) ?? 'Document'}
						isSelected={document.documentId === documentId}
					/>
				))}
			</div>
			{failedDocuments.map((document) => (
				<p key={document.stepId} css={failureStyle}>
					{`${document.failureMessage?.replace(/\.\s*$/, '') ?? `${documentTypes?.labelOf(document.documentTypeId) ?? 'Document'} n’a pas pu être généré`} : réessayez depuis la préparation.`}
				</p>
			))}
		</React.Fragment>
	);
};

export default ReviewStageTabs;
